import React, { useEffect, useState } from "react";
import { getUser } from "../../helpers/AuthService";
import { useNavigate } from "react-router-dom";
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaTicketAlt } from "react-icons/fa";

import Loading from "../../components/Loading";
import { SkeletonMyTickets } from "../../components/Skeleton";
import api from "../../helpers/Api";
import MessageError from "../../components/MessageError";
import moment from "moment";
import "moment/locale/es";

export function Render({ ticketData }) {
    const navigate = useNavigate();

    const URL_ORIGIN = process.env.NODE_ENV === "development"
        ? process.env.REACT_APP_API_URL_DEV
        : process.env.REACT_APP_API_URL;

    return (
        <div className="bg-[#0f0e17] min-h-screen py-12 px-4">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-10">
                    <span className="text-brand font-mono font-semibold text-sm uppercase tracking-widest">Mi cuenta</span>
                    <h2 className="text-white font-display font-bold text-3xl sm:text-4xl mt-2">Mis Boletos</h2>
                </div>

                {ticketData?.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="w-20 h-20 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-5">
                            <FaTicketAlt className="text-gray-500 text-3xl" />
                        </div>
                        <p className="text-gray-400 text-lg">Aún no tienes boletos comprados.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {(Array.isArray(ticketData) ? ticketData : []).map((purchase) => {
                            const totalTickets = purchase.tickets.length;
                            const event = purchase.event;
                            const primaryImage = event?.event_images?.find((img) => img.is_primary);

                            return (
                                <div
                                    key={purchase.purchase_id}
                                    className="group bg-[#16151c] border border-white/10 hover:border-brand/30 rounded-2xl overflow-hidden transition-all duration-300 shadow-xl"
                                >
                                    <div className="relative h-44 bg-[#0f0e17] overflow-hidden">
                                        {primaryImage && (
                                            <img
                                                src={`${URL_ORIGIN}/image-proxy?id=${primaryImage.id}`}
                                                alt={primaryImage.image_name || "Event image"}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#16151c] via-[#16151c]/20 to-transparent" />
                                        <div className="absolute bottom-3 left-3 right-3">
                                            <p className="text-white font-display font-bold text-base leading-tight drop-shadow-md line-clamp-2">
                                                {event?.name}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="p-4 flex flex-col gap-2">
                                        <div className="flex items-center gap-2 text-gray-300 text-xs capitalize">
                                            <FaCalendarAlt className="text-brand shrink-0" />
                                            {moment(event?.event_date).locale("es").format("dddd, D [de] MMMM YYYY")}
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-300 text-xs">
                                            <FaClock className="text-brand shrink-0" />
                                            {moment(event?.event_date).utcOffset(0, true).format("hh:mm a")}
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-300 text-xs">
                                            <FaMapMarkerAlt className="text-brand shrink-0" />
                                            <span className="truncate">{event?.location}</span>
                                        </div>

                                        <div className="flex items-center gap-2 mt-1 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 w-fit">
                                            <FaTicketAlt className="text-green-400 text-xs" />
                                            <span className="text-green-400 text-xs font-semibold">
                                                {totalTickets} boleto{totalTickets !== 1 ? "s" : ""}
                                            </span>
                                        </div>

                                        <button
                                            onClick={() => navigate(`/mytickets/detail/${purchase.purchase_id}`)}
                                            className="mt-2 w-full event-one__btn thm-btn py-2 text-sm"
                                        >
                                            Ver detalles
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function MyTickets() {
    const [ticketData, setTicketData] = useState(null);
    const [error, setError] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const userLogged = await getUser();
                if (!userLogged) { setTicketData([]); return; }
                const ticketDataResponse = await api.get(`Customer/MyTicket`);
                setTicketData(ticketDataResponse.data);
            } catch (error) {
                if (error.response?.status === 401) navigate(`/`);
                setError(true);
            }
        };
        fetchData();
    }, []);

    if (error) return <MessageError title="Ocurrió un error al cargar tus boletos." description="Por favor, contacta soporte técnico." />;
    if (!ticketData) return <SkeletonMyTickets />;
    return <Render ticketData={ticketData} />;
}
